import React, { createContext, useContext, useEffect, useState } from 'react';

interface AdminTheme {
  mode: 'light' | 'dark';
  primaryColor: string;
  accentColor: string;
}

interface AdminThemeContextType {
  theme: AdminTheme;
  toggleTheme: () => void;
  setThemeColor: (color: string) => void;
  isCustomizing: boolean;
  setIsCustomizing: (customizing: boolean) => void;
}

const defaultTheme: AdminTheme = {
  mode: 'light',
  primaryColor: '#3b82f6',
  accentColor: '#10b981',
};

const AdminThemeContext = createContext<AdminThemeContextType | undefined>(undefined);

export function AdminThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<AdminTheme>(() => {
    const saved = localStorage.getItem('admin-theme');
    return saved ? JSON.parse(saved) : defaultTheme;
  });
  const [isCustomizing, setIsCustomizing] = useState(false);

  useEffect(() => {
    localStorage.setItem('admin-theme', JSON.stringify(theme));
    
    // Apply theme to CSS variables
    const root = document.documentElement;
    root.style.setProperty('--admin-primary', theme.primaryColor);
    root.style.setProperty('--admin-accent', theme.accentColor);
    
    // Apply dark mode class
    if (theme.mode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => ({
      ...prev,
      mode: prev.mode === 'light' ? 'dark' : 'light'
    }));
  };

  const setThemeColor = (color: string) => {
    setTheme(prev => ({
      ...prev,
      primaryColor: color
    }));
  };

  return (
    <AdminThemeContext.Provider 
      value={{ 
        theme, 
        toggleTheme, 
        setThemeColor, 
        isCustomizing, 
        setIsCustomizing 
      }}
    >
      {children}
    </AdminThemeContext.Provider>
  );
}

export function useAdminTheme() {
  const context = useContext(AdminThemeContext);
  if (context === undefined) {
    throw new Error('useAdminTheme must be used within an AdminThemeProvider');
  }
  return context;
}